import React, { useState } from "react";
import PropTypes from "prop-types";
import "./card-payment.scss";
import visaLogo from "../../assets/flags/visa.png";
import mastercardLogo from "../../assets/flags/mastercard.png";
import amexLogo from "../../assets/flags/amex.png";
import defaultLogo from "../../assets/flags/default.png";
import chip from "../../assets/flags/chip.png";
import { useForm, Controller } from "react-hook-form";
import useUserStore from "../../storeModules/useUserStore";
import { nomeRazaoRegex, removeNonDigits } from "../../utils/regex";
import { PatternFormat } from "react-number-format";
import valid from "card-validator";
import { cpf } from "cpf-cnpj-validator";
import useConsorcioStore from "../../storeModules/useConsorcioStore";
import useBotControlStore from "../../storeModules/useBotControlStore";
import { sendPaymentCartao } from "../../api/requests";
import LoadingDots from "../loadingDots";
import approvedPaymentImage from "../../assets/images/cartao-aprovado.gif";
import findStep from "../../screens/bot/stepModules/findStep";
import { tagPurchase, tagStatusPagamento } from "../../utils/sendTag";
import { metodosPagamentoNames, statusPagamentoCartao } from "../../utils/constants";
import noConnectionHandler from "../../utils/noConnectionHandler";
import InputMask from "react-input-mask";
import validateCnpj from "../../utils/validateCnpj";
import removeSpecialCharacters from "../../utils/removeSpecialCharacters";
import { getRemoteValueEnv } from "../../setup";
import trackConversacaoStep from "../../utils/trackConversacaoStep";
import {
  CARD_PAYMENT_REFUSED_GIF_URL,
} from "../../utils/paymentImageUrls";

const refusedPaymentImageUrl = CARD_PAYMENT_REFUSED_GIF_URL;

// ============= UTILITY FUNCTIONS =============
const getCardFlag = (cardNumber) => {
  const number = cardNumber.replace(/\s+/g, "");

  if (/^4[0-9]{12}(?:[0-9]{3})?$/.test(number)) {
    return "visa";
  } else if (/^5[1-5][0-9]{14}$/.test(number)) {
    return "mastercard";
  } else if (/^3[47][0-9]{13}$/.test(number)) {
    return "amex";
  } else {
    return "default";
  }
};

const cardLogoMap = {
  visa: visaLogo,
  mastercard: mastercardLogo,
  amex: amexLogo,
  default: defaultLogo,
};

// ============= VALIDATION FUNCTIONS =============
const validateNomeTitular = (value, isPessoaFisica) => {
  const nomeTitularErrorMessage = isPessoaFisica
    ? "Nome inválido"
    : "Razão Social inválida";
  return nomeRazaoRegex.test(value) || nomeTitularErrorMessage;
};

const validateCartaoNumero = (value) => {
  return valid.number(value).isValid || "Cartão inválido";
};

const validateDataValidade = (value) => {
  const validDate = valid.expirationDate(value);
  return validDate.isValid || validDate.isPotentiallyValid || "Data inválida";
};

const validateCpfCnpj = (value) => {
  value = removeSpecialCharacters(value);

  if (value.length <= 11) {
    return (value && cpf.isValid(value)) || "CPF inválido";
  } else {
    return (value && validateCnpj(value)) || "CNPJ inválido";
  }
};

const validateParcelas = (value) => {
  return (!!value && value !== 0) || "Selecione parcelas";
};

// ============= CREDIT CARD DISPLAY COMPONENT =============
const CreditCardDisplay = ({ isFlipped, getValues }) => {
  return (
    <div
      data-testid="payment-card"
      className={`credit-card ${isFlipped ? "flipped" : ""}`}
    >
      <div className="card-front">
        <div className="form-row">
          <div className="form-column">
            <img src={chip} alt="Card Flag" width={50} className="card-logo" />
          </div>
          <div className="form-column position-img">
            <img
              src={cardLogoMap[getCardFlag(getValues("cartaoNumero") || "")]}
              alt="Card Flag"
              width={80}
              className="card-logo"
            />
          </div>
        </div>
        <div>
          <div className="text-number">Número</div>
          <div data-testid="visual-card-number" className="card-number">
            {getValues("cartaoNumero") || "XXXX XXXX XXXX XXXX"}
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className="text-number">Titular</div>
            <div data-testid="visual-card-name" className="card-name">
              {getValues("cartaoNomeTitular") || "NOME DO TITULAR"}
            </div>
          </div>
          <div className="form-column">
            <div className="text-number">Validade</div>
            <div data-testid="visual-date-expiration" className="card-expiry">
              {getValues("cartaoValidadeMesAno") || "MM/AA"}
            </div>
          </div>
        </div>
      </div>
      <div className="card-back">
        <div className="barra-black"></div>
        <div className="card-cvv">
          {getValues("cartaoCodigoSeguranca") || "XXX"}
        </div>
        <div className="logo-cvv">
          <img
            src={defaultLogo}
            alt="Card Flag"
            width={70}
            className="card-logo"
          />
        </div>
      </div>
    </div>
  );
};

CreditCardDisplay.propTypes = {
  isFlipped: PropTypes.bool.isRequired,
  getValues: PropTypes.func.isRequired,
};

// ============= FORM FIELD COMPONENTS =============
const NameField = ({ register, errors, trigger, isPessoaFisica }) => (
  <>
    <input
      {...register("cartaoNomeTitular", {
        required: {
          value: true,
          message: "Campo obrigatório",
        },
        maxLength: {
          value: 30,
          message: "Numero máximo de caracteres excedido",
        },
        validate: {
          value: (value) => validateNomeTitular(value, isPessoaFisica),
        },
      })}
      autoFocus
      onBlur={() => trigger("cartaoNomeTitular")}
      className={`payment-form__input ${
        errors.cartaoNomeTitular ? "payment-form__input--error" : ""
      }`}
      type="text"
      name="cartaoNomeTitular"
      placeholder="Nome Impresso no Cartão"
    />
    <p data-testid="input-name-error" className="payment-form__message">
      {errors?.cartaoNomeTitular ? errors.cartaoNomeTitular.message : ""}
    </p>
  </>
);

NameField.propTypes = {
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
  isPessoaFisica: PropTypes.bool.isRequired,
};

const CardNumberField = ({ control, errors, trigger }) => (
  <>
    <Controller
      control={control}
      name="cartaoNumero"
      rules={{
        required: { value: true, message: "Campo obrigatório" },
        validate: {
          value: validateCartaoNumero,
        },
      }}
      render={({ field: { onChange, value } }) => (
        <PatternFormat
          format="#### #### #### ####"
          mask="_"
          onChange={onChange}
          onBlur={() => trigger("cartaoNumero")}
          value={value}
          placeholder="Número do Cartão"
          className={`payment-form__input ${
            errors?.cartaoNumero ? "payment-form__input--error" : ""
          }`}
        />
      )}
    />
    <p data-testid="input-number-error" className="payment-form__message">
      {errors?.cartaoNumero ? errors.cartaoNumero.message : ""}
    </p>
  </>
);

CardNumberField.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
};

const ExpiryAndCvvFields = ({
  control,
  register,
  errors,
  trigger,
  handleFocusCvv,
  handleBlurCvv,
}) => (
  <div className="form-row">
    <div className="form-row__input-wrapper">
      <Controller
        control={control}
        name="cartaoValidadeMesAno"
        rules={{
          required: { value: true, message: "Campo obrigatório" },
          minLength: { value: 7, message: "Data inválida" },
          validate: {
            value: validateDataValidade,
          },
        }}
        render={({ field: { onChange, value } }) => (
          <PatternFormat
            className={`payment-form__input ${
              errors.cartaoValidadeMesAno ? "payment-form__input--error" : ""
            }`}
            type="text"
            placeholder="Validade (MM/AAAA)"
            onChange={(e) => {
              if (e.target.value === "__/____") {
                return onChange("");
              }
              return onChange(e.target.value);
            }}
            onBlur={() => trigger("cartaoValidadeMesAno")}
            value={value}
            format="##/####"
            mask="_"
          />
        )}
      />
      <p
        data-testid="input-expiration-date-error"
        className="payment-form__message"
      >
        {errors?.cartaoValidadeMesAno
          ? errors.cartaoValidadeMesAno.message
          : ""}
      </p>
    </div>

    <div className="form-row__input-wrapper">
      <input
        {...register("cartaoCodigoSeguranca", {
          required: {
            value: true,
            message: "Campo obrigatório",
          },
        })}
        className={`payment-form__input ${
          errors.cartaoCodigoSeguranca ? "payment-form__input--error" : ""
        }`}
        type="text"
        name="cartaoCodigoSeguranca"
        placeholder="CVV"
        onFocus={handleFocusCvv}
        onBlur={() => {
          handleBlurCvv();
          trigger("cartaoCodigoSeguranca");
        }}
        maxLength={3}
      />
      <p className="payment-form__message">
        {errors?.cartaoCodigoSeguranca
          ? errors.cartaoCodigoSeguranca.message
          : ""}
      </p>
    </div>
  </div>
);

ExpiryAndCvvFields.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
  handleFocusCvv: PropTypes.func.isRequired,
  handleBlurCvv: PropTypes.func.isRequired,
};

const CpfAndParcelasFields = ({ control, errors, trigger }) => {
  const isAlphaNumericCNPJ = getRemoteValueEnv(
    "isAlphaNumericCNPJ_dev",
    "isAlphaNumericCNPJ_prod",
  ).asBoolean();

  return (
    <div className="form-row">
      <div className="form-row__input-wrapper">
        <Controller
          control={control}
          name="cartaoCpfTitular"
          rules={{
            required: { value: true, message: "Campo obrigatório" },
            validate: {
              value: validateCpfCnpj,
            },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              {isAlphaNumericCNPJ ? (
                <InputMask
                  className={`payment-form__input ${
                    errors.cartaoCpfTitular ? "payment-form__input--error" : ""
                  }`}
                  type="text"
                  placeholder="CPF/CNPJ"
                  maskChar={null}
                  mask={
                    removeSpecialCharacters(value).length <= 11
                      ? "***.***.***-******"
                      : "**.***.***/****-99"
                  }
                  value={value}
                  onChange={(e) => {
                    onChange(
                      removeSpecialCharacters(e.target.value).toUpperCase(),
                    );
                  }}
                  onBlur={() => trigger("cartaoCpfTitular")}
                  alwaysShowMask={false}
                />
              ) : (
                <PatternFormat
                  className={`payment-form__input ${
                    errors.cartaoCpfTitular ? "payment-form__input--error" : ""
                  }`}
                  type="text"
                  placeholder="CPF/CNPJ"
                  format={
                    value.length <= 11
                      ? "###.###.###-###"
                      : "##.###.###/####-##"
                  }
                  mask=" "
                  onChange={(e) =>
                    onChange(e.target.value.replace(removeNonDigits, ""))
                  }
                  onBlur={() => trigger("cartaoCpfTitular")}
                  value={value}
                />
              )}
            </>
          )}
        />

        <p className="payment-form__message">
          {errors?.cartaoCpfTitular ? errors.cartaoCpfTitular.message : ""}
        </p>
      </div>

      <div className="form-row__input-wrapper">
        <Controller
          name={"cartaoParcelas"}
          control={control}
          rules={{
            required: { value: true, message: "Campo obrigatório" },
            validate: {
              value: validateParcelas,
            },
          }}
          render={({ field: { onChange } }) => (
            <select
              className="payment-form__input"
              onChange={onChange}
              onBlur={() => trigger("cartaoParcelas")}
            >
              <option>Parcelas</option>
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={3}>3x</option>
            </select>
          )}
        />
        <p className="payment-form__message">
          {errors?.cartaoParcelas ? errors.cartaoParcelas.message : ""}
        </p>
      </div>
    </div>
  );
};

CpfAndParcelasFields.propTypes = {
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  trigger: PropTypes.func.isRequired,
};

const ActionButtons = ({ formState, isLoading, onClose, setCurrentStep }) => (
  <div className="space-buttons">
    <button
      className={`option-button option-button__fullwidth ${
        formState.isValid ? "" : "form-submit--disabled"
      } ${isLoading ? "form-submit--unclickable" : ""}`}
      type="submit"
    >
      {isLoading ? <LoadingDots size="small" /> : "FINALIZAR PAGAMENTO"}
    </button>
    <button
      className={`option-button option-button__fullwidth secondary ${
        isLoading ? "form-submit--disabled" : ""
      }`}
      onClick={() => {
        trackConversacaoStep({
          stepName: "formulario_cartao",
          userMessage: "VOLTAR",
          userOnly: true,
        });
        onClose();
        setCurrentStep(findStep("alterar_forma_pagamento").index);
      }}
    >
      VOLTAR
    </button>
  </div>
);

ActionButtons.propTypes = {
  formState: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

// ============= MAIN COMPONENT =============
const PaymentModal = ({ isVisible, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [, setUpdateCard] = useState(0);

  const fetchedPropostaId = useConsorcioStore(
    (state) => state.fetchedProposta.propostaID,
  );
  const { tipoPessoa } = useUserStore((state) => state.contrato);
  const isPessoaFisica = tipoPessoa === 0;

  const setMessages = useBotControlStore((state) => state.setMessages);
  const setCurrentStep = useBotControlStore((state) => state.setCurrentStep);
  const setPayment = useUserStore((state) => state.setPayment);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    control,
    formState,
    trigger,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      cartaoBandeira: "",
      cartaoCodigoSeguranca: "",
      cartaoCpfTitular: "",
      cartaoNomeTitular: "",
      cartaoNumero: "",
      cartaoValidadeMes: 0,
      cartaoValidadeAno: 0,
      cartaoParcelas: 0,
      cartaoId: 0,
      propostaId: 0,
      cartaoValidadeMesAno: "",
    },
  });

  React.useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      setUpdateCard((state) => state + 1);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  if (!isVisible) return null;

  // ============= EVENT HANDLERS =============
  const handleFocusCvv = () => {
    setIsFlipped(true);
  };

  const handleBlurCvv = () => {
    setIsFlipped(false);
  };

  const createPaymentProps = (data) => {
    const {
      cartaoCodigoSeguranca,
      cartaoCpfTitular,
      cartaoNomeTitular,
      cartaoNumero,
      cartaoParcelas,
      cartaoId,
      cartaoValidadeMesAno,
    } = data;

    const [mes, ano] = cartaoValidadeMesAno.split("/");

    return {
      cartaoBandeira: "VISA",
      cartaoCodigoSeguranca,
      cartaoCpfTitular,
      cartaoNomeTitular,
      cartaoNumero,
      cartaoValidadeMes: mes,
      cartaoValidadeAno: ano,
      cartaoParcelas,
      cartaoId,
      propostaId: fetchedPropostaId,
    };
  };

  const handlePaymentSuccess = (data) => {
    tagStatusPagamento(statusPagamentoCartao.aprovado);
    setPayment(data.cartaoAutorizacao, "cartaoAutorizacao");

    const successText = `<b>Seu pagamento foi realizado com sucesso!</b> <br><br> nº ${data.cartaoAutorizacao}`;

    setMessages({
      sender: "bot",
      text: successText,
      component: (
        <img
          className="payment-image"
          src={approvedPaymentImage}
          alt="Pagamento aprovado"
        />
      ),
      name: "pagamento_aprovado",
      shouldPersistComponent: true,
    });

    trackConversacaoStep({
      stepName: "pagamento_aprovado",
      botMessage: successText,
    });

    tagPurchase(metodosPagamentoNames["CARTÃO"]);
    setCurrentStep(findStep("compra_finalizada").index);
  };

  const handlePaymentPending = () => {
    tagStatusPagamento(statusPagamentoCartao.pendente);
    setMessages({
      sender: "bot",
      text:
        "<b>Seu pagamento está sendo validado :)</b> <br><br> Aguarde a confirmação que será enviada para o seu e-mail. Qualquer dúvida, entre em contato com nosso time de especialistas.",
      name: "pagamento_pendente",
    });
    trackConversacaoStep({
      stepName: "pagamento_pendente",
      botMessage:
        "<b>Seu pagamento está sendo validado :)</b> <br><br> Aguarde a confirmação que será enviada para o seu e-mail. Qualquer dúvida, entre em contato com nosso time de especialistas.",
    });
    useBotControlStore.setState({ skipStepTracking: true });
    setCurrentStep(findStep("pagamento_pendente").index);
  };

  const handlePaymentError = () => {
    tagStatusPagamento(statusPagamentoCartao.recusado);
    setMessages({
      sender: "bot",
      text: "<b>Pagamento recusado :(</b> <br/><br/> O quê você deseja fazer?",
      component: (
        <img
          className="card-payment"
          src={refusedPaymentImageUrl}
          alt="Cartão recusado"
        />
      ),
      name: "cartao_recusado",
      shouldPersistComponent: true,
    });
    trackConversacaoStep({
      stepName: "cartao_recusado",
      botMessage:
        "<b>Pagamento recusado :(</b> <br/><br/> O quê você deseja fazer?",
    });
    useBotControlStore.setState({ skipStepTracking: true });
    setCurrentStep(findStep("cartao_recusado").index);
  };

  const onSubmit = async (data) => {
    await noConnectionHandler()
      .then(async () => {
        setIsLoading(true);

        const paymentProps = createPaymentProps(data);

        try {
          const response = await sendPaymentCartao(paymentProps);
          const { data: responseData } = response.data;

          if (response.status === 200) {
            handlePaymentSuccess(responseData);
          } else {
            handlePaymentPending();
          }
        } catch (error) {
          handlePaymentError();
        } finally {
          onClose();
          setIsLoading(false);
        }
      })
      .finally(() => {
        onClose();
      });
  };

  // ============= RENDER =============
  return (
    <div className="payment-modal">
      <div className="modal-content">
        <CreditCardDisplay isFlipped={isFlipped} getValues={getValues} />

        <form className="payment-form" onSubmit={handleSubmit(onSubmit)}>
          <NameField
            register={register}
            errors={errors}
            trigger={trigger}
            isPessoaFisica={isPessoaFisica}
          />

          <CardNumberField
            control={control}
            errors={errors}
            trigger={trigger}
          />

          <ExpiryAndCvvFields
            control={control}
            register={register}
            errors={errors}
            trigger={trigger}
            handleFocusCvv={handleFocusCvv}
            handleBlurCvv={handleBlurCvv}
          />

          <CpfAndParcelasFields
            control={control}
            errors={errors}
            trigger={trigger}
          />

          <ActionButtons
            formState={formState}
            isLoading={isLoading}
            onClose={onClose}
            setCurrentStep={setCurrentStep}
          />
        </form>
      </div>
    </div>
  );
};

PaymentModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PaymentModal;
